import DOMPurifyImport from 'dompurify';

/**
 * Sanitize admin-authored HTML (the rich_text form field) before it is rendered
 * to inductees, so a careless or compromised author can't inject script or
 * other active content. DOMPurify strips scripts, event handlers and dangerous
 * URLs; the hook forces every link to open in a new tab safely.
 */

interface Purifier {
  sanitize: (dirty: string, cfg?: Record<string, unknown>) => string;
  addHook: (entryPoint: string, cb: (node: Node) => void) => void;
}

// Bound lazily: in the browser build the default export is the ready instance,
// while under jsdom/node it is a factory that must be given a window. Either
// way `window` only has to exist when we first sanitize, not at import time.
let purifier: Purifier | null = null;
function getPurifier(): Purifier {
  if (purifier) return purifier;
  const imp = DOMPurifyImport as unknown as Partial<Purifier> & ((w: Window) => Purifier);
  const p: Purifier = typeof imp.sanitize === 'function' ? (imp as Purifier) : imp(window);
  p.addHook('afterSanitizeAttributes', (node) => {
    if (node instanceof Element && node.tagName === 'A') {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
  });
  purifier = p;
  return p;
}

export function sanitizeHtml(html: string): string {
  return getPurifier().sanitize(html, {
    // Allow only http(s)/mailto/tel/anchor/relative links, never javascript: etc.
    ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|tel:|#|\/)/i,
  });
}
